import React from 'react';
import OptionsCommon from 'components/quote_builder_widget/form/options/OptionsCommon.jsx';
import BaseDrawingMaterialChooser from 'components/quote_builder_widget/shared/BaseDrawingMaterialChooser';
import BaseHotStampingChooser from 'components/quote_builder_widget/shared/BaseHotStampingChooser';
import BaseSubmitButton from 'components/quote_builder_widget/shared/SubmitButton';
import LoadingSpinner from 'components/quote_builder_widget/shared/LoadingSpinner';
import ImageCarousel from 'components/quote_builder_widget/shared/ImageCarousel';
import { usePrintingColorChoice } from 'components/quote_builder_widget/shared/BasePrintingColorChooser';

// The island themes (islands, bento, steps) group the form into one card for each part of the quote: dimensions,
// quantity, material, printing and extras. They share this markup and differ only in their stylesheet. An island
// renders only when it has a field to show, and its number comes from a CSS counter, so an absent island leaves no gap.
const buildIslandTheme = (styles, { maxWidth, defaultHighlightColor }) => {
  const Island = ({ kind, title, accessory, children }) => (
    <section className={styles.island} data-widget-element="island" data-island={kind}>
      <header className={styles.islandHeader} data-widget-element="island-header">
        <span className={styles.islandNumber} data-widget-element="island-number" aria-hidden="true" />
        <h4 className={styles.islandTitle} data-widget-element="island-title">{title}</h4>
        {accessory}
      </header>
      <div className={styles.islandBody} data-widget-element="island-body">
        {children}
      </div>
    </section>
  );

  const MaterialIsland = ({ children }) => (
    <Island kind="material" title={t('quote_builder_widget.widget_material')}>{children}</Island>
  );

  const ExtrasIsland = ({ children }) => (
    <Island kind="extras" title={t('quote_builder_widget.extras')}>{children}</Island>
  );

  const QuoteBuilderLayout = ({ images, loading, children }) => (
    <div className={styles.layoutContainer} data-widget-element="layout-container">
      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          <div className={styles.imageSection} data-widget-element="image-section">
            <h3 className={styles.title} data-widget-element="title">{t('quote_builder_widget.get_a_quote')}</h3>
            <ImageCarousel images={images} imageClassName={styles.previewImage} />
          </div>
          <div className={styles.formSection} data-widget-element="form-section">
            {children}
          </div>
        </>
      )}
    </div>
  );

  const DrawingOptionsLayout = ({ quantity, unit, options }) => (
    <>
      <Island kind="dimensions" title={t('quote_builder_widget.dimensions')} accessory={unit}>{options}</Island>
      <Island kind="quantity" title={t('quote_builder_widget.quantity')}>{quantity}</Island>
    </>
  );

  // The unit toggle sits in the dimensions island's header.
  const SelectUnit = ({ unit, setUnit }) => (
    <div className={styles.unitToggle} role="group" aria-label={t('my_drawings.options_unit_short')} data-widget-element="unit-toggle">
      <input type="hidden" name="unit" value={unit} />
      {['mm', 'in'].map((choice) => (
        <button
          key={choice}
          type="button"
          className={`${styles.unitButton} ${unit === choice ? styles.unitButtonActive : ''}`}
          onClick={() => setUnit(() => choice)}
          aria-pressed={unit === choice}
          data-widget-element="unit-button"
          data-active={unit === choice}
        >
          {t(`drawing.unit_${choice}`)}
        </button>
      ))}
    </div>
  );

  // The island's title names the field.
  const SelectQuantity = () => {
    const [value, setValue] = React.useState(1000);

    return (
      <div className={styles.inputWrapper} data-widget-element="input-wrapper">
        <input
          type="number"
          name="quantity"
          className={`${styles.input} ${styles.quantityInput}`}
          value={value}
          onChange={(event) => setValue(() => event.target.value)}
          aria-label={t('quote_builder_widget.quantity')}
          data-widget-element="input"
        />
      </div>
    );
  };

  const FormGroup = ({ inputType, label, children }) => (
    <div className={styles.formGroup} data-widget-element="form-group" data-input-type={inputType}>
      <label className={styles.label} data-widget-element="label">{label}</label>
      <div className={styles.inputWrapper} data-widget-element="input-wrapper">
        {children}
      </div>
    </div>
  );

  const OptionLengthWithUnit = ({ id, drawing, option, unit }) => {
    const formattedValue = (value) => {
      const toFormat = parseFloat(value);
      return (Number.isNaN(toFormat) ? 0 : toFormat).toFixed(unit === 'in' ? 4 : 2);
    };

    const [isVisible, value, onChange] = OptionsCommon({ formattedValue, id, drawing, option, unit });

    if (!isVisible) {
      return <input type="hidden" name={option.name} value={value} />;
    }

    return (
      <FormGroup inputType="length" label={option.presented_name}>
        <input
          type="text"
          name={option.name}
          className={`${styles.input} ${styles.inputWithSuffix}`}
          value={value}
          onChange={onChange}
          onBlur={(event) => onChange({ target: { value: formattedValue(event.target.value) } })}
          readOnly={option.readonly}
          autoComplete="off"
          inputMode="decimal"
          data-widget-element="input"
          data-has-suffix="true"
        />
        <span className={styles.unitSuffix} data-widget-element="unit-suffix">{unit}</span>
      </FormGroup>
    );
  };

  const OptionBoolean = ({ id, drawing, option, unit }) => {
    const [isVisible, value, onChange] = OptionsCommon({ formattedValue: (value) => value, id, drawing, option, unit });

    if (!isVisible) {
      return <input type="hidden" name={option.name} value={value} />;
    }

    return (
      <FormGroup inputType="boolean" label={option.presented_name}>
        <input
          name={'__' + option.name}
          type="checkbox"
          className={styles.checkbox}
          value="1"
          onChange={onChange}
          checked={value == 1}
          autoComplete="off"
          readOnly={option.readonly_option}
          data-widget-element="checkbox"
        />
        <input type="hidden" name={option.name} value={value} />
      </FormGroup>
    );
  };

  const OptionIntegerWithRange = ({ id, drawing, option, unit }) => {
    const [isVisible, value, onChange] = OptionsCommon({ formattedValue: (value) => value, id, drawing, option, unit });

    const [choices] = React.useState(() => {
      if (option.possible_values.length > 0) {
        return option.possible_values;
      }
      return Array.from({ length: option.max_int_value - option.min_int_value + 1 }, (_, index) => index + option.min_int_value);
    });

    if (!isVisible) {
      return <input type="hidden" name={option.name} value={value} />;
    }

    return (
      <FormGroup inputType="integer" label={option.presented_name}>
        <select name={option.name} value={value} onChange={onChange} className={styles.input} data-widget-element="select">
          {choices.map((choice) => <option key={choice} value={choice}>{choice}</option>)}
        </select>
      </FormGroup>
    );
  };

  const OptionSelect = ({ id, drawing, option, unit }) => {
    const [isVisible, value, onChange] = OptionsCommon({ formattedValue: (value) => value, id, drawing, option, unit });

    if (!isVisible) {
      return <input type="hidden" name={option.name} value={value} />;
    }

    return (
      <FormGroup inputType="select" label={option.presented_name}>
        <select name={option.name} value={value} onChange={onChange} className={styles.input} data-widget-element="select">
          {(option.select_choices || []).map((choice) => (
            <option key={choice.value} value={choice.value}>{choice.description}</option>
          ))}
        </select>
      </FormGroup>
    );
  };

  const DrawingMaterialChooser = ({ widgetIdentifier, drawingId, onMaterialChange, onCartonMaterialSelected }) => (
    <BaseDrawingMaterialChooser
      widgetIdentifier={widgetIdentifier}
      drawingId={drawingId}
      onMaterialChange={onMaterialChange}
      onCartonMaterialSelected={onCartonMaterialSelected}
      styles={styles}
      selectClassName={styles.input}
      Wrapper={MaterialIsland}
    />
  );

  // The island's title names the dropdown, so it has no label of its own.
  const PrintingColorChooser = ({ widgetIdentifier, cartonMaterialUserPriceId }) => {
    const { ready, hasChoices, selectedKey, onChange, options, hiddenFields } = usePrintingColorChoice(widgetIdentifier, cartonMaterialUserPriceId);

    if (!ready) {
      return null;
    }

    if (!hasChoices) {
      return hiddenFields;
    }

    return (
      <Island kind="printing" title={t('quote_builder.printing')}>
        <div className={styles.inputWrapper} data-widget-element="input-wrapper">
          <select
            value={selectedKey}
            onChange={onChange}
            className={styles.input}
            aria-label={t('quote_builder.printing')}
            data-widget-element="select"
          >
            {options}
          </select>
        </div>
        {hiddenFields}
      </Island>
    );
  };

  const HotStampingChooser = ({ widgetIdentifier, cartonMaterialUserPriceId, drawingId, unit }) => (
    <BaseHotStampingChooser
      widgetIdentifier={widgetIdentifier}
      cartonMaterialUserPriceId={cartonMaterialUserPriceId}
      drawingId={drawingId}
      unit={unit}
      styles={styles}
      selectClassName={styles.input}
      lengthInputClassName={`${styles.input} ${styles.inputWithSuffix}`}
      Wrapper={ExtrasIsland}
    />
  );

  const SubmitButton = ({ buttonState, onClick }) => (
    <div className={styles.submitRow} data-widget-element="form-group">
      <BaseSubmitButton buttonState={buttonState} onClick={onClick} buttonClassName={styles.submitButton} />
    </div>
  );

  return {
    maxWidth,
    defaultHighlightColor,
    QuoteBuilderLayout,
    DrawingOptionsLayout,
    SelectUnit,
    SelectQuantity,
    OptionLengthWithUnit,
    OptionBoolean,
    OptionIntegerWithRange,
    OptionSelect,
    DrawingMaterialChooser,
    PrintingColorChooser,
    HotStampingChooser,
    SubmitButton,
  };
};

export default buildIslandTheme;
